import { useMemo , memo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ClipboardList, Plus, Syringe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { HoverTooltip } from "@/components/ui/tooltip";
import { formatPlanStatusLabel, getStatusBadge } from "@/utils/helpers";
import type { CarePlanV2, PatientV2, TreatmentPlanV2 } from "../types";
import { SpecialInstructionsSection } from "../PatientSummary/SpecialInstructionsSection";

type Props = {
  patient: PatientV2 | null;
  patientId: string;
  carePlans: CarePlanV2[];
  treatmentPlans?: TreatmentPlanV2[];
  canAssignCaretaker?: boolean;
  canManageSpecialInstructions?: boolean;
  onPatientUpdate?: (patient: PatientV2) => void;
  onCarePlanClick?: () => void;
  onTxPlanClick?: () => void;
};

function recentPlan(carePlans: CarePlanV2[]) {
  if (!carePlans.length) return null;
  return [...carePlans].sort((a, b) =>
    String(b.updatedAt ?? b.createdAt ?? "").localeCompare(
      String(a.updatedAt ?? a.createdAt ?? ""),
    ),
  )[0];
}

function recentTreatmentPlan(treatmentPlans: TreatmentPlanV2[]) {
  if (!treatmentPlans.length) return null;
  return [...treatmentPlans].sort((a, b) =>
    String(b.updatedAt ?? b.createdAt ?? "").localeCompare(
      String(a.updatedAt ?? a.createdAt ?? ""),
    ),
  )[0];
}

export function PatientRightPanelFields({
  patient,
  patientId,
  carePlans,
  treatmentPlans = [],
  canManageSpecialInstructions = false,
  onCarePlanClick,
  onTxPlanClick,
}: Props) {
  console.log("Patient Tab 3A -------------------------------------------------------------------------------");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const latestCarePlan = useMemo(() => recentPlan(carePlans), [carePlans]);
  const latestTxPlan = useMemo(() => recentTreatmentPlan(treatmentPlans), [treatmentPlans]);

  const patientPrograms = (patient?.programs ?? []).map((program) => String(program).toLowerCase());
  const programsJoined = patientPrograms.join(",");
  const hasCcmOrPcm = programsJoined.includes("ccm") || programsJoined.includes("pcm");
  const hasRpm = programsJoined.includes("rpm");
  const hasTcm = patientPrograms.includes("tcm");
  const isActiveOrInactive =
    patient?.status === "active" ||
    patient?.status === "newly_added" ||
    patient?.status === "inactive";

  const lastCarePlanStatus = String(
    (patient as Record<string, unknown>)?.lastCarePlanStatus ?? latestCarePlan?.status ?? "",
  );
  const lastTreatmentPlanStatus = String(
    (patient as Record<string, unknown>)?.lastTreatmentPlanStatus ?? latestTxPlan?.status ?? "",
  );
  const showCarePlanButton =
    canManageSpecialInstructions && hasCcmOrPcm && isActiveOrInactive;
  const showTxPlanButton =
    canManageSpecialInstructions && hasRpm && isActiveOrInactive;
  const showTcmPlanButton =
    canManageSpecialInstructions && hasTcm && isActiveOrInactive;
  const planButtonCount = [showCarePlanButton, showTxPlanButton, showTcmPlanButton].filter(
    Boolean,
  ).length;

  const handleAddCarePlan = () => {
    const isPcmFromUrl = searchParams.get("plan") === "pcm";
    const isPcmOnly =
      patientPrograms.includes("pcm") && !patientPrograms.includes("ccm");
    const isPcmCarePlan = isPcmFromUrl || isPcmOnly;

    navigate(`/careplan/${patient?._id || patientId}${isPcmCarePlan ? "?plan=pcm" : ""}`, {
      state: { patientData: patient },
    });
  };

  const handleAddTxPlan = () => {
    navigate(`/treatment-plan/${patient?._id || patientId}`, {
      state: { patientData: patient },
    });
  };

  const handleAddTcmPlan = () => {
    navigate(`/tcm-plan/${patient?._id || patientId}`, {
      state: { patientData: patient },
    });
  };

  const planStatusSection = useMemo(() => {
    const patientStatus = String(patient?.status ?? "")
      .trim()
      .toLowerCase();
    if (!patient || patientStatus === "pending") return null;

    const care = String(patient?.lastCarePlanStatus ?? "")
      .trim()
      .toLowerCase();
    const treat = String(patient?.lastTreatmentPlanStatus ?? "")
      .trim()
      .toLowerCase();
    const programs = Array.isArray(patient?.programs) ? patient.programs : [];
    const showCare = care === "followup" || care === "pending";
    const showTreat = programs.includes("rpm") && treat === "pending";

    if (!showCare && !showTreat) return null;

    const carePlanStatus =
      patient.lastCarePlanStatus === "followUp"
        ? "Pending Follow Up"
        : patient.lastCarePlanStatus === "pending"
          ? "Pending"
          : "";

    const careBadgeKey =
      patient.lastCarePlanStatus === "followUp"
        ? "pendingFollowUp"
        : patient.lastCarePlanStatus;
    const careBadge = getStatusBadge(careBadgeKey);
    const treatBadge = getStatusBadge(patient.lastTreatmentPlanStatus);

    return (
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {showCare ? (
          <button
            type="button"
            onClick={onCarePlanClick}
            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
          >
            <div className="flex min-w-0 items-center gap-2">
              <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Care Plan</span>
            </div>
            <span
              className="shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-medium leading-tight"
              style={{
                backgroundColor: careBadge.bgColor,
                color: careBadge.mainColor,
                borderColor: careBadge.mainColor,
              }}
            >
              {formatPlanStatusLabel(String(carePlanStatus))}
            </span>
          </button>
        ) : null}

        {showCare && showTreat ? <Separator /> : null}

        {showTreat ? (
          <button
            type="button"
            onClick={onTxPlanClick}
            className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Syringe className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Tx Plan</span>
            </div>
            <span
              className="shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-medium leading-tight"
              style={{
                backgroundColor: treatBadge.bgColor,
                color: treatBadge.mainColor,
                borderColor: treatBadge.mainColor,
              }}
            >
              {formatPlanStatusLabel(String(patient.lastTreatmentPlanStatus))}
            </span>
          </button>
        ) : null}
      </div>
    );
  }, [patient, onCarePlanClick, onTxPlanClick]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 p-2 pb-0">
        {planButtonCount > 0 ? (
          <div
            className={
              planButtonCount > 1
                ? "grid min-w-0 flex-1 grid-cols-2 gap-1.5"
                : "grid min-w-0 flex-1 grid-cols-1 gap-1.5"
            }
          >
            {showCarePlanButton ? (
              <HoverTooltip
                content={
                  lastCarePlanStatus === "added"
                    ? "Care Plan already added. Please edit the existing care plan."
                    : ""
                }
                className="bg-gray-200 text-black"
                arrowColor="bg-gray-200 fill-gray-200"
              >
                <Button
                  type="button"
                  size="sm"
                  className="h-7 w-full rounded-md px-2 text-[11px] font-semibold"
                  onClick={handleAddCarePlan}
                  disabled={lastCarePlanStatus === "added"}
                >
                  <Plus className="h-3 w-3" />
                  Care Plan
                </Button>
              </HoverTooltip>
            ) : null}

            {showTxPlanButton ? (
              <HoverTooltip
                content={
                  lastTreatmentPlanStatus === "added"
                    ? "Treatment Plan already added. Please edit the existing treatment plan."
                    : ""
                }
                className="bg-gray-200 text-black"
                arrowColor="bg-gray-200 fill-gray-200"
              >
                <Button
                  type="button"
                  size="sm"
                  className="h-7 w-full rounded-md px-2 text-[11px] font-semibold"
                  onClick={handleAddTxPlan}
                  disabled={lastTreatmentPlanStatus === "added"}
                >
                  <Plus className="h-3 w-3" />
                  Tx Plan
                </Button>
              </HoverTooltip>
            ) : null}

            {showTcmPlanButton ? (
              <Button
                type="button"
                size="sm"
                className="h-7 w-full rounded-md px-2 text-[11px] font-semibold"
                onClick={handleAddTcmPlan}
              >
                <Plus className="h-3 w-3" />
                TCM Plan
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="min-w-0 flex-1" />
        )}

        
      </div>

        {/* <div className="px-2">
          {planStatusSection}
        </div> */}
      
      <Separator className="w-full" />

      <div className="px-2 pb-2">
        <SpecialInstructionsSection
          patientId={patient?._id || patientId}
          canManage={canManageSpecialInstructions}
        />
      </div>
    </div>
  );
}

export const PatientRightPanelFieldsMemo = memo(PatientRightPanelFields);
